/**
 * PATH: apps/qa/src/lib/api/reports.ts
 * PURPOSE: QA Reports and Report Drafts API
 */

import { api, normaliseList } from './helpers'
import type { Report } from '@/types/qa'

export interface ReportListStats {
  total?: number
  qa_packs?: number
  generating?: number
  downloads?: number
}

export type ApiBoundaryState = 'forbidden' | 'conflict' | 'invalid' | 'error'

export function getApiBoundaryState(error: unknown): ApiBoundaryState {
  const status = error && typeof error === 'object' && 'response' in error
    ? (error as { response?: { status?: number } }).response?.status
    : undefined
  if (status === 403) return 'forbidden'
  if (status === 409) return 'conflict'
  if (status === 422) return 'invalid'
  return 'error'
}

export function isDraftConflict(error: unknown): boolean {
  return getApiBoundaryState(error) === 'conflict'
}

export const reportApi = {
  getReports: async (params?: { inspection_id?: string; page?: number; page_size?: number }): Promise<{ results: Report[]; count: number; stats?: ReportListStats }> => {
    const response = await api().get('/qa/reports/', { params })
    return normaliseList(response.data)
  },
  getReport: async (id: string): Promise<Report> => {
    const response = await api().get(`/qa/reports/${id}/`)
    return response.data
  },
  generateReport: async (data: { inspection_id: string; report_type: string; report_name?: string; scope?: Record<string, unknown> }): Promise<Report> => {
    const response = await api().post('/qa/reports/generate/', data)
    return response.data
  },
  regenerateReport: async (id: string): Promise<Report> => {
    const response = await api().post(`/qa/reports/${id}/regenerate/`)
    return response.data
  },
  shareReport: async (id: string, data: { permissions: Array<'review' | 'comment' | 'sign' | 'download'>; recipient_email?: string; expires_in_hours?: number }): Promise<{ id: string; portal_url: string; token: string; expires_at: string }> => {
    const response = await api().post(`/qa/reports/${id}/share/`, data)
    return response.data
  },
  emailReport: async (id: string, data: { to: string[]; cc?: string[]; subject?: string; message?: string; attach_pdf?: boolean }): Promise<{ email_id: number; status: string; pdf_attached: boolean; message: string }> => {
    const response = await api().post(`/qa/reports/${id}/email/`, data)
    return response.data
  },
  getDownloadUrl: (id: string) => `${api().defaults.baseURL}/qa/reports/${id}/download/`,
  getArtifactUrl: (assetId: string) => `${api().defaults.baseURL}/files/${assetId}/download/`,
}

// Report Draft types
export interface ReportSectionItem { id: string; section_type: string; content: string; sort_order: number; is_auto_generated: boolean; last_edited_by: string | null; last_edited_by_name: string; last_edited_at: string | null; word_count: number }
export interface SignOffData { signer_name: string; designation: string; company: string; signature_data_url: string; photo_data_url?: string; signed_at: string }
export interface IncludedSections { floors?: string[]; areas?: string[]; include_ncrs?: boolean; include_evidence?: boolean }
export interface ReportDraftItem { id: string; inspection: string; inspection_name: string; job_id: string; title: string; status: 'draft' | 'review' | 'final' | 'exported'; created_by: string | null; created_by_name: string; created_at: string; updated_at: string; exported_report: string | null; sections: ReportSectionItem[]; sign_off_data?: SignOffData; version: number; included_sections: IncludedSections; user_notes: Record<string, string>; fact_overrides?: ReportOverride[] }
export interface ReportDraftListItem { id: string; inspection: string; inspection_name: string; job_id: string; title: string; status: string; created_by_name: string; created_at: string; updated_at: string; section_count: number; completed_sections: number; sign_off_data?: SignOffData; version: number; included_sections: IncludedSections; user_notes: Record<string, string> }
export interface ExactExportResult { draft_id: string; report_id?: string; revision_id?: string; pdf_file_id?: string; status: 'generated' | 'generating' | 'queued' }
export interface ReportOverride {
  id: string
  draft: string
  revision: string | null
  field_path: string
  original_value: unknown
  current_value: unknown
  reason: string
  actor: string
  actor_name: string
  created_at: string
}
export interface CreateReportOverride {
  original_content: string
  current_content: string
  reason: string
  expected_version: number
}

export const reportDraftApi = {
  getDrafts: async (params?: { page?: number; page_size?: number }): Promise<{ results: ReportDraftListItem[]; count: number }> => {
    const response = await api().get('/qa/report-drafts/', { params })
    return normaliseList(response.data)
  },
  getDraft: async (id: string): Promise<ReportDraftItem> => {
    const response = await api().get(`/qa/report-drafts/${id}/`)
    return response.data
  },
  createDraft: async (data: { inspection: string; title: string }): Promise<ReportDraftItem> => {
    const response = await api().post('/qa/report-drafts/', data)
    return response.data
  },
  updateDraft: async (id: string, data: { expected_version: number; title?: string; status?: string; sign_off_data?: SignOffData; included_sections?: IncludedSections; user_notes?: Record<string, string> }): Promise<ReportDraftItem> => {
    const response = await api().patch(`/qa/report-drafts/${id}/`, data)
    return response.data
  },
  saveVersion: async (id: string, data: { expected_version: number; title?: string; included_sections?: IncludedSections; user_notes?: Record<string, string> }): Promise<ReportDraftItem> => {
    const response = await api().post(`/qa/report-drafts/${id}/save-version/`, data)
    return response.data
  },
  deleteDraft: async (id: string): Promise<void> => { await api().delete(`/qa/report-drafts/${id}/`) },
  updateSection: async (draftId: string, sectionType: string, content: string, expectedVersion: number): Promise<ReportSectionItem> => {
    const response = await api().put(`/qa/report-drafts/${draftId}/section/${sectionType}/`, { content, expected_version: expectedVersion })
    return response.data
  },
  exportMarkdown: async (id: string) => { const r = await api().post(`/qa/report-drafts/${id}/export/?format=markdown`); return r.data },
  exportPdf: async (id: string): Promise<ExactExportResult> => { const r = await api().post(`/qa/report-drafts/${id}/export/`); return r.data },
  getExactPreviewUrl: (id: string, revisionId?: string) => `${api().defaults.baseURL}/qa/report-drafts/${id}/exact-preview/${revisionId ? `?revision=${encodeURIComponent(revisionId)}` : ''}`,
  getOverrides: async (id: string, sectionType: string): Promise<ReportOverride[]> => {
    const draft = await reportDraftApi.getDraft(id)
    return (draft.fact_overrides || []).filter((item) => item.field_path === `sections.${sectionType}.content`)
  },
  createOverride: async (id: string, sectionType: string, data: CreateReportOverride): Promise<{ draft_version: number; override: ReportOverride }> => {
    const r = await api().post(`/qa/report-drafts/${id}/overrides/`, {
      expected_version: data.expected_version,
      field_path: `sections.${sectionType}.content`,
      original_value: data.original_content,
      current_value: data.current_content,
      reason: data.reason,
    })
    return r.data
  },
  /** @deprecated Use exportMarkdown or exportPdf */
  exportDraft: async (id: string) => { const r = await api().post(`/qa/report-drafts/${id}/export/?format=markdown`); return r.data },
}
